import React, { useState, useCallback, useEffect } from 'react';
import { Toaster } from 'sonner';
import { translations } from './translations';
import {
  Header,
  Hero,
  IntroSection,
  WhoForSection,
  PatentedSection,
  Configurator,
  Gallery,
  VideoSection,
  ContactForm,
  Footer
} from './components/sections';
import './App.css';

const seoMeta = {
  cs: {
    title: 'UrbanCage® | Modulární multifunkční hřiště a klece na fotbal a basketbal | Česko',
    description: 'UrbanCage® – modulární sportovní hřiště a arény s evropským patentem. Antivandalské klece na fotbal, basketbal a padel. Sportovní infrastruktura pro obce, školy a městské prostředí. Akrylátové povrchy, EPDM, umělá tráva.'
  },
  en: {
    title: 'UrbanCage® | Modular Multi-Sport Courts & Football and Basketball Cages | Czech Republic',
    description: 'UrbanCage® – EU-patented modular sports courts and arenas. Vandal-resistant cages for football, basketball and padel. Sports infrastructure for municipalities, schools and urban spaces. Acrylic surfaces, EPDM, artificial grass.'
  }
};

const getInitialLanguage = () => {
  const param = new URLSearchParams(window.location.search).get('lang');
  return param === 'en' ? 'en' : 'cs';
};

function App() {
  const [language, setLanguage] = useState(getInitialLanguage);
  const [configSummary, setConfigSummary] = useState('');
  const t = translations[language];

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = seoMeta[language].title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', seoMeta[language].description);
    const metaTitle = document.querySelector('meta[name="title"]');
    if (metaTitle) metaTitle.setAttribute('content', seoMeta[language].title);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', language === 'en' ? 'https://urbancage.cz/?lang=en' : 'https://urbancage.cz/');
  }, [language]);

  const scrollToSection = useCallback((id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Handler for reactive config changes (updates on every selection change)
  const handleConfigChange = useCallback((summary) => {
    setConfigSummary(summary);
  }, []);

  // Handler for when user clicks "Send Inquiry" in configurator
  const handleSendInquiry = useCallback(() => {
    // Just scroll to form - config is already up to date
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="urban-cage-app">
      {/* Toast notifications */}
      <Toaster position="top-center" richColors />
      
      {/* Skip to main content link (accessibility) */}
      <a href="#main-content" className="skip-link">
        {language === 'cs' ? 'Přejít k obsahu' : 'Skip to main content'}
      </a>
      
      {/* Header */}
      <Header 
        t={t} 
        language={language} 
        setLanguage={setLanguage} 
        scrollToSection={scrollToSection} 
      />

      <main id="main-content" role="main">
        {/* Hero Section */}
        <Hero 
          t={t} 
          language={language} 
          scrollToSection={scrollToSection} 
        />

        {/* Intro Text Section */}
        <IntroSection t={t} />

        {/* Who Is Urban Cage For */}
        <WhoForSection t={t} language={language} />

        {/* UrbanCage in Numbers */}
        <PatentedSection t={t} />

        {/* Cage Configurator */}
        <Configurator 
          t={t} 
          language={language} 
          onConfigChange={handleConfigChange}
          onSendInquiry={handleSendInquiry}
        />

        {/* Image Gallery */}
        <Gallery t={t} language={language} />

        {/* Video Section */}
        <VideoSection t={t} language={language} />

        {/* Contact Form */}
        <ContactForm 
          t={t} 
          language={language} 
          configurationSummary={configSummary}
        />
      </main>

      {/* Footer */}
      <Footer 
        t={t} 
        language={language} 
        setLanguage={setLanguage} 
        scrollToSection={scrollToSection} 
      />
    </div>
  );
}

export default App;
